import type { FunctionComponent } from 'react';

import { NotFoundPage } from '@kanda-libs/ks-design-library-new';
import {
  actions,
  createAction,
  createAppDispatchHook,
  createRoutedApp,
} from '@kanda-libs/ks-frontend-services';
import type { CreatePageArgs } from '@kanda-libs/ks-frontend-services/src/middleware/helpers';

import { ErrorBoundary } from '@highlight-run/react';
import BannerWrapper from 'components/BannerWrapper';
import { PageKeys, URLS } from 'config';
import Apply from 'pages/Apply';
import ApplyRedirect from 'pages/Apply/ApplyRedirect';
import AuthLink from 'pages/AuthLink';
import CompleteIntroduction from 'pages/CompleteIntroduction';
import ConsentSuccess from 'pages/Consent/Success';
import ContinueApplication from 'pages/ContinueApplication';
import CustomCode from 'pages/CustomCode';
import Expired from 'pages/Expired';
import Home from 'pages/Home';
import Job from 'pages/Job';
import Login from 'pages/Login';
import Logout from 'pages/Logout';
import PayByBacs from 'pages/PayByBacs';
import PayByCard from 'pages/PayByCard';
import PaymentSuccess from 'pages/PaymentSuccess';
import Quote from 'pages/Quote';
import Staff from 'pages/Staff';
import StaffLogin from 'pages/StaffLogin';
import {
  Address,
  Details,
  Employment,
  MoreInfo,
  Referred,
  Success,
  Welcome,
} from 'pages/StartApplication/pages';
import Status from 'pages/Status';
import { AppDispatch, RootState, store } from 'store';
import { getCreditId } from 'utils';

import AppWrapper from '../AppWrapper';
import KandaErrorBoundary from '../ErrorBoundary';

const args: Record<PageKeys, CreatePageArgs<RootState>> = {
  login: {
    path: URLS.login,
    PageComponent: Login,
    loadingDependencies: [],
    initialDataActions: [],
  },
  home: {
    path: URLS.home,
    PageComponent: Home,
    requiredRoles: ['*'],
    loadingDependencies: ['job'],
    initialDataActions: [createAction(actions.getJobs)],
  },
  staff: {
    path: URLS.staff,
    PageComponent: Staff,
    requiredRoles: ['staff'],
    loadingDependencies: [],
    initialDataActions: [],
  },
  staffLogin: {
    path: URLS.staffLogin,
    PageComponent: StaffLogin,
    loadingDependencies: [],
    initialDataActions: [],
  },
  expired: {
    path: URLS.expired,
    PageComponent: Expired,
    loadingDependencies: [],
    initialDataActions: [],
  },
  logout: {
    path: URLS.logout,
    PageComponent: Logout,
    requiredRoles: ['*'],
    loadingDependencies: [],
    initialDataActions: [],
  },
  job: {
    path: URLS.job,
    PageComponent: Job,
    idRequired: true,
    loadingDependencies: ['job', 'jobCompanyInfo', 'introduction'],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (data) => {
        const job = data.data;
        const intro = Boolean(job?.introduced_to);
        const jobId = job?.id;
        return [
          ...(intro ? [createAction(actions.getIntroduction, {}, true)] : []),
          ...(jobId && !intro
            ? [
                createAction(actions.infoWorkType, {
                  params: { job_id: jobId },
                  forceReload: true,
                }),
              ]
            : []),
        ];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  completeIntroduction: {
    path: URLS.completeIntroduction,
    PageComponent: CompleteIntroduction,
    idRequired: true,
    loadingDependencies: ['job', 'introduction', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.getIntroduction, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  quote: {
    path: URLS.quote,
    PageComponent: Quote,
    idRequired: true,
    requiredRoles: ['*'],
    loadingDependencies: ['job', 'lead'],
    initialDataActions: [
      createAction(actions.getJob, {}, true, (r) => {
        const job = r.data;
        const id = getCreditId(job);
        const quotedTo = job?.quoted_to;
        return [
          ...(id
            ? [createAction(actions.getCredit, { params: { id } }, true)]
            : []),
          ...(quotedTo
            ? [
                createAction(
                  actions.getCredit,
                  { params: { id: quotedTo } },
                  true,
                ),
              ]
            : []),
        ];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  reapply: {
    path: URLS.reapply,
    PageComponent: Job,
    idRequired: true,
    loadingDependencies: ['job', 'jobCompanyInfo', 'introduction'],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (data) => {
        const job = data.data;
        const intro = Boolean(job?.introduced_to);
        const jobId = job?.id;
        return [
          ...(intro ? [createAction(actions.getIntroduction, {}, true)] : []),
          ...(jobId && !intro
            ? [
                createAction(actions.infoWorkType, {
                  params: { job_id: jobId },
                  forceReload: true,
                }),
              ]
            : []),
        ];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  authLink: {
    path: URLS.authLink,
    PageComponent: AuthLink,
    loadingDependencies: [],
    initialDataActions: [],
  },
  paymentSuccess: {
    path: URLS.paymentSuccess,
    PageComponent: PaymentSuccess,
    idRequired: true,
    requiredRoles: ['*'],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true, (job) => {
        const id = getCreditId(job.data);
        if (!id) return [];
        return [createAction(actions.getCredit, { params: { id } }, true)];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  payByCard: {
    path: URLS.payByCard,
    PageComponent: PayByCard,
    idRequired: true,
    loadingDependencies: ['job'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  payByBank: {
    path: URLS.payByBank,
    PageComponent: PayByBacs,
    idRequired: true,
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyForFinance: {
    path: URLS.applyForFinance,
    PageComponent: Apply,
    idRequired: true,
    loadingDependencies: [
      'job',
      'jobCompanyInfo',
      'introduction',
      'infoGetCache',
    ],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (job) => {
        const intro = Boolean(job?.data?.introduced_to);
        if (!intro) return [];
        return [createAction(actions.getIntroduction, {}, true)];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
      createAction(actions.infoGetCache),
    ],
    requiredRoles: ['*'],
  },
  applyForFinanceWelcome: {
    path: URLS.applyForFinanceWelcome,
    PageComponent: ApplyRedirect,
    idRequired: true,
    loadingDependencies: [],
    initialDataActions: [],
    // PageComponent: Apply,
    // idRequired: true,
    // loadingDependencies: ['job', 'jobCompanyInfo', 'introduction'],
    // initialDataActions: [
    //   createAction(actions.getJob, { chainedRequest: true }, true, (job) => {
    //     const intro = Boolean(job?.data?.introduced_to);
    //     if (!intro) return [];
    //     return [createAction(actions.getIntroduction, {}, true)];
    //   }),
    //   createAction(actions.jobCompanyInfo, {}, true),
    // ],
    requiredRoles: ['*'],
  },
  applyForFinanceTermsAndConditions: {
    path: URLS.applyForFinanceTermsAndConditions,
    // PageComponent: ApplyRedirect,
    // idRequired: true,
    // loadingDependencies: [],
    // initialDataActions: [],
    PageComponent: Apply,
    idRequired: true,
    loadingDependencies: [
      'job',
      'jobCompanyInfo',
      'introduction',
      'infoGetCache',
    ],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (job) => {
        const intro = Boolean(job?.data?.introduced_to);
        if (!intro) return [];
        return [createAction(actions.getIntroduction, {}, true)];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
      createAction(actions.infoGetCache),
    ],
    requiredRoles: ['*'],
  },
  applyForFinanceDetails: {
    path: URLS.applyForFinanceDetails,
    // PageComponent: ApplyRedirect,
    // idRequired: true,
    // loadingDependencies: [],
    // initialDataActions: [],
    PageComponent: Apply,
    idRequired: true,
    loadingDependencies: [
      'job',
      'jobCompanyInfo',
      'introduction',
      'infoGetCache',
    ],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (job) => {
        const intro = Boolean(job?.data?.introduced_to);
        if (!intro) return [];
        return [createAction(actions.getIntroduction, {}, true)];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
      createAction(actions.infoGetCache),
    ],
    requiredRoles: ['*'],
  },
  continueApplication: {
    path: URLS.continueApplication,
    PageComponent: ContinueApplication,
    idRequired: true,
    requiredRoles: ['*'],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  status: {
    path: URLS.status,
    PageComponent: Status,
    idRequired: true,
    requiredRoles: ['*'],
    loadingDependencies: ['job', 'jobCompanyInfo', 'credit'],
    initialDataActions: [
      createAction(actions.getJob, { chainedRequest: true }, true, (job) => {
        const intro = Boolean(job?.data?.introduced_to);
        if (!intro) return [];
        return [createAction(actions.getIntroduction, {}, true)];
      }),
      createAction(actions.jobCompanyInfo, {}, true),
      createAction(actions.getCredits, {}),
    ],
  },
  applyWelcome: {
    path: URLS.applyWelcome,
    PageComponent: Welcome,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true, (job) => [
        createAction(
          actions.getLead,
          { params: { id: job?.data.quoted_to as string } },
          true,
        ),
      ]),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyDetails: {
    path: URLS.applyDetails,
    PageComponent: Details,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyAddress: {
    path: URLS.applyAddress,
    PageComponent: Address,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyEmployment: {
    path: URLS.applyEmployment,
    PageComponent: Employment,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applySuccess: {
    path: URLS.applySuccess,
    PageComponent: Success,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyMoreInfo: {
    path: URLS.applyMoreInfo,
    PageComponent: MoreInfo,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  applyReferred: {
    path: URLS.applyReferred,
    PageComponent: Referred,
    idRequired: true,
    requiredRoles: [],
    loadingDependencies: ['job', 'jobCompanyInfo'],
    initialDataActions: [
      createAction(actions.getJob, {}, true),
      createAction(actions.jobCompanyInfo, {}, true),
    ],
  },
  customCode: {
    path: URLS.customCode,
    PageComponent: CustomCode,
    loadingDependencies: [],
    initialDataActions: [],
  },
  consentSuccess: {
    path: URLS.consentSuccess,
    PageComponent: ConsentSuccess,
    loadingDependencies: [],
    initialDataActions: [],
  },
};

const {
  pages,
  router: { Router },
} = createRoutedApp<RootState, PageKeys>(
  store,
  args,
  NotFoundPage,
  BannerWrapper,
);

const useAppDispatch = createAppDispatchHook<RootState, AppDispatch>();

export { pages, useAppDispatch };

const App: FunctionComponent = function () {
  return (
    <AppWrapper>
      <ErrorBoundary>
        <KandaErrorBoundary>
          <Router />
        </KandaErrorBoundary>
      </ErrorBoundary>
    </AppWrapper>
  );
};

export default App;
